import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Utensils, LayoutGrid, ChevronRight, ChevronLeft, Sparkles, Calendar, Loader2, Inbox,
  AlertTriangle, RefreshCw, Flame, Beef, Wheat, Droplet, ArrowRight, Crown, ChefHat, Gift, Salad,
} from 'lucide-react';
import { useClientAuth } from '../../context/ClientAuthContext';
import { useTourRegister } from '../../utils/tourNav';
import { PlanCreditCard } from './PlanCreditCard';
import { RecipesLibrary } from './RecipesLibrary';
import { PersonalRecipesTab } from './PersonalRecipesTab';
import { MealPlanResultView } from './MealPlanResultView';
import { AppMenuTile } from '../../components/AppMenuTile';

const GOAL_LABELS = {
  weight_loss: ['Perte de poids', 'Weight loss'],
  muscle_gain: ['Prise de masse', 'Muscle gain'],
  maintenance: ['Maintien', 'Maintenance'],
  recomposition: ['Recomposition', 'Body recomp'],
};

const Badge = ({ color, icon: Icon, children }) => {
  const styles = {
    yellow: 'bg-yellow-500/15 border-yellow-500/40 text-yellow-300',
    pink: 'bg-primary/15 border-primary/40 text-primary',
    zinc: 'bg-zinc-700/40 border-white/10 text-zinc-300',
  };
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold border ${styles[color]}`}>
      {Icon && <Icon className="w-3 h-3" />} {children}
    </span>
  );
};

/** Onglet "Nutrition" ultra pro : hub d'icônes -> Plan alimentaire (générateur) / Historique des calculs. */
export const NutritionHub = ({ en }) => {
  const L = (fr, eng) => (en ? eng : fr);
  const { authFetch } = useClientAuth();
  const navigate = useNavigate();
  const api = useCallback((path, opts) => authFetch(path, opts).then((r) => (r.ok ? r.json() : Promise.reject(r))), [authFetch]);
  const onUpgrade = () => navigate('/suivi-pro');

  const [view, setView] = useState('hub'); // hub | plan | plan-detail | history | recipes
  useTourRegister('setNutritionView', setView);
  const [selectedPlanCalcId, setSelectedPlanCalcId] = useState(null);
  const [selectedPlanPackage, setSelectedPlanPackage] = useState('meal_plan');
  const [selectedPlanReturnView, setSelectedPlanReturnView] = useState('plan');
  const [calcs, setCalcs] = useState(null);
  const [loadErr, setLoadErr] = useState('');
  const [suggestion, setSuggestion] = useState(null);
  const [openRecipeId, setOpenRecipeId] = useState(null);
  const [newRecipe, setNewRecipe] = useState(null);
  const [recipesCount, setRecipesCount] = useState(null);

  useEffect(() => {
    let active = true;
    api('/api/auth/my-calculations')
      .then((d) => { if (active) setCalcs(d.calculations || []); })
      .catch(() => { if (active) setLoadErr(L('Impossible de charger vos calculs.', 'Could not load your calculations.')); });
    api('/api/recipes/suggestion-of-the-day')
      .then((d) => { if (active) setSuggestion(d); })
      .catch(() => {}); // silencieux (non premium ou erreur réseau)
    api('/api/recipes/new-of-the-day')
      .then((d) => { if (active) setNewRecipe(d); })
      .catch(() => {}); // silencieux (aucune nouvelle recette récente, non premium, ou erreur réseau)
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/recipes/count`).then((r) => r.json()).then((d) => { if (active) setRecipesCount(d.count); }).catch(() => {});
    return () => { active = false; };
  }, []);

  const fmtDate = (iso) => {
    if (!iso) return '';
    try { return new Date(iso).toLocaleDateString(en ? 'en-GB' : 'fr-CH', { day: '2-digit', month: 'short', year: 'numeric' }); }
    catch { return iso; }
  };

  if (view === 'hub') {
    return (
      <div className="space-y-5" data-testid="nutrition-hub">
        <h2 className="text-xl font-black text-white flex items-center gap-2">
          <Utensils className="w-5 h-5 text-primary" /> {L('Espace Nutrition', 'Nutrition Hub')}
        </h2>
        <div className="space-y-3">
          <AppMenuTile icon={Utensils} gradient="bg-gradient-to-br from-yellow-500 to-primary" testId="tile-meal-plan"
            label={L('Plan alimentaire', 'Meal plan')} sub={L('Générer et consulter vos plans', 'Generate and view your plans')}
            onClick={() => setView('plan')} />
          <AppMenuTile icon={LayoutGrid} gradient="bg-gradient-to-br from-cyan-500 to-blue-600" testId="tile-calc-history"
            label={L('Historique des calculs', 'Calculation history')} sub={L('Tous vos calculs de calories', 'All your calorie calculations')}
            onClick={() => setView('history')} />
          <AppMenuTile icon={ChefHat} gradient="bg-gradient-to-br from-primary to-pink-600" testId="tile-recipes"
            label={L('Recettes protéinées', 'High-protein recipes')} sub={recipesCount ? L(`+ de ${recipesCount} recettes prêtes à logger en un clic`, `${recipesCount}+ recipes ready to log in one tap`) : L('Recettes prêtes à logger en un clic', 'Recipes ready to log in one tap')}
            onClick={() => { setOpenRecipeId(null); setView('recipes'); }}
            badge={(
              <span className="hidden sm:inline-flex shrink-0 items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold bg-yellow-500/20 border border-yellow-500/40 text-yellow-300 whitespace-nowrap" data-testid="recipes-tile-price-badge">
                <Crown className="w-3 h-3" /> {L('Inclus dès 9.90 CHF', 'Included from 9.90 CHF')}
              </span>
            )} />
          <AppMenuTile icon={Salad} gradient="bg-gradient-to-br from-emerald-500 to-cyan-600" testId="tile-personal-recipes"
            label={L('Mes recettes personnelles', 'My personal recipes')} sub={L('Générées depuis vos plans alimentaires', 'Generated from your meal plans')}
            onClick={() => setView('personal-recipes')} />
        </div>

        {newRecipe && (
          <motion.button
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
            onClick={() => { setOpenRecipeId(newRecipe.id); setView('recipes'); }}
            className="w-full flex items-center gap-4 rounded-2xl bg-emerald-500/[0.06] backdrop-blur-xl border border-emerald-500/30 hover:border-emerald-400/60 p-4 text-left transition-colors"
            data-testid="new-recipe-of-the-day-card"
          >
            <img src={newRecipe.image_url} alt="" className="w-16 h-16 rounded-xl object-cover shrink-0" />
            <div className="min-w-0">
              <p className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-emerald-400"><Gift className="w-3 h-3" /> {L('Nouvelle recette !', 'New recipe!')}</p>
              <p className="font-bold text-white truncate mt-0.5">{en ? newRecipe.name_en : newRecipe.name_fr}</p>
              <p className="text-xs text-zinc-500 flex items-center gap-1.5 mt-0.5"><Flame className="w-3 h-3 text-primary" /> {Math.round(newRecipe.macros.calories)} kcal · {Math.round(newRecipe.macros.protein)}g {L('protéines', 'protein')}</p>
            </div>
            <ChevronRight className="w-5 h-5 text-zinc-600 ml-auto shrink-0" />
          </motion.button>
        )}

        {suggestion && (
          <motion.button
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
            onClick={() => { setOpenRecipeId(suggestion.id); setView('recipes'); }}
            className="w-full flex items-center gap-4 rounded-2xl bg-white/[0.035] backdrop-blur-xl border border-white/10 hover:border-primary/40 p-4 text-left transition-colors"
            data-testid="suggestion-of-the-day-card"
          >
            <img src={suggestion.image_url} alt="" className="w-16 h-16 rounded-xl object-cover shrink-0" />
            <div className="min-w-0">
              <p className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-yellow-300"><Sparkles className="w-3 h-3" /> {L('Suggestion du jour', 'Suggestion of the day')}</p>
              <p className="font-bold text-white truncate mt-0.5">{en ? suggestion.name_en : suggestion.name_fr}</p>
              <p className="text-xs text-zinc-500 flex items-center gap-1.5 mt-0.5"><Flame className="w-3 h-3 text-primary" /> {Math.round(suggestion.macros.calories)} kcal · {Math.round(suggestion.macros.protein)}g {L('protéines', 'protein')}</p>
            </div>
            <ChevronRight className="w-5 h-5 text-zinc-600 ml-auto shrink-0" />
          </motion.button>
        )}
      </div>
    );
  }

  const BackBtn = () => (
    <button onClick={() => { setOpenRecipeId(null); setView('hub'); }} className="inline-flex items-center gap-1.5 text-zinc-400 hover:text-white text-sm mb-4" data-testid="back-to-nutrition-hub-btn">
      <ChevronLeft className="w-4 h-4" /> {L('Retour', 'Back')}
    </button>
  );

  if (view === 'recipes') {
    return (
      <div data-testid="nutrition-recipes-view">
        <BackBtn />
        <RecipesLibrary api={api} en={en} onUpgrade={onUpgrade} initialRecipeId={openRecipeId} authFetch={authFetch} />
      </div>
    );
  }

  if (view === 'personal-recipes') {
    return (
      <div data-testid="nutrition-personal-recipes-view">
        <BackBtn />
        <PersonalRecipesTab api={api} en={en} />
      </div>
    );
  }

  if (view === 'plan-detail' && selectedPlanCalcId) {
    return (
      <div data-testid="nutrition-plan-detail-view">
        <MealPlanResultView
          calculationId={selectedPlanCalcId}
          packageType={selectedPlanPackage}
          en={en}
          onBack={() => { setSelectedPlanCalcId(null); setView(selectedPlanReturnView); }}
        />
      </div>
    );
  }

  if (view === 'plan') {
    const plans = (calcs || []).filter((c) => c.meal_plan_status);
    return (
      <div data-testid="nutrition-plan-view">
        <BackBtn />
        <PlanCreditCard api={api} en={en} onViewPlan={(calcId) => { setSelectedPlanCalcId(calcId); setSelectedPlanPackage('meal_plan'); setSelectedPlanReturnView('plan'); setView('plan-detail'); }} />

        {plans.length > 0 && (
          <section className="mt-8" data-testid="my-plans-section">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="w-5 h-5 text-yellow-400" />
              <h2 className="text-lg font-bold">{L('Mes plans nutritionnels', 'My nutrition plans')}</h2>
              <span className="text-xs text-zinc-500">({plans.length})</span>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              {plans.map((c, i) => {
                const isReady = c.meal_plan_status === 'ready';
                const isGenerating = c.meal_plan_status === 'generating';
                const isError = c.meal_plan_status === 'error';
                return (
                  <motion.div key={`plan-${c.calculation_id}`} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, delay: i * 0.04 }}
                    className={`rounded-2xl border p-5 transition-colors ${isReady ? 'border-yellow-400/30 bg-gradient-to-br from-yellow-500/10 to-zinc-900/60 hover:border-yellow-400/60' : isGenerating ? 'border-primary/30 bg-gradient-to-br from-primary/10 to-zinc-900/60' : 'border-red-500/30 bg-gradient-to-br from-red-500/10 to-zinc-900/60'}`}
                    data-testid={`plan-card-${c.meal_plan_status}`}>
                    <div className="flex items-center justify-between gap-2 mb-3">
                      <span className="inline-flex items-center gap-1.5 text-xs text-zinc-400"><Calendar className="w-3.5 h-3.5" /> {fmtDate(c.created_at)}</span>
                      {isReady && <Badge color="yellow" icon={Sparkles}>{L('Plan prêt', 'Plan ready')}</Badge>}
                      {isGenerating && <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold border border-primary/40 bg-primary/15 text-primary"><Loader2 className="w-3 h-3 animate-spin" /> {L('Génération en cours', 'Generating')}</span>}
                      {isError && <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold border border-red-500/40 bg-red-500/15 text-red-300"><AlertTriangle className="w-3 h-3" /> {L('Erreur', 'Error')}</span>}
                    </div>
                    <p className="font-bold text-lg text-white">{(GOAL_LABELS[c.goal] || [c.goal, c.goal])[en ? 1 : 0]}</p>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 mt-2 text-sm text-zinc-300">
                      <span className="inline-flex items-center gap-1.5"><Flame className="w-4 h-4 text-primary" /> {Math.round(c.target_calories)} kcal</span>
                      {c.protein_g != null && <span className="inline-flex items-center gap-1.5"><Beef className="w-4 h-4 text-pink-400" /> {Math.round(c.protein_g)}g</span>}
                      {c.carbs_g != null && <span className="inline-flex items-center gap-1.5"><Wheat className="w-4 h-4 text-yellow-400" /> {Math.round(c.carbs_g)}g</span>}
                      {c.fat_g != null && <span className="inline-flex items-center gap-1.5"><Droplet className="w-4 h-4 text-emerald-400" /> {Math.round(c.fat_g)}g</span>}
                    </div>
                    {isReady && <button onClick={() => { setSelectedPlanCalcId(c.calculation_id); setSelectedPlanPackage('meal_plan'); setSelectedPlanReturnView('plan'); setView('plan-detail'); }} className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-yellow-300 hover:text-yellow-200 transition-colors" data-testid="view-plan-link">{L('Consulter le plan', 'Open plan')} <ArrowRight className="w-4 h-4" /></button>}
                    {isGenerating && (
                      <div className="mt-4">
                        <p className="text-xs text-zinc-400 mb-2">{L('Votre plan est en cours de création. Cela peut prendre jusqu\'à 4 minutes.', 'Your plan is being created. This can take up to 4 minutes.')}</p>
                        <button onClick={() => { setSelectedPlanCalcId(c.calculation_id); setSelectedPlanPackage('meal_plan'); setSelectedPlanReturnView('plan'); setView('plan-detail'); }} className="inline-flex items-center gap-1.5 text-sm font-bold text-primary hover:text-pink-400 transition-colors" data-testid="view-plan-progress">{L('Voir la progression', 'View progress')} <ArrowRight className="w-4 h-4" /></button>
                      </div>
                    )}
                    {isError && (
                      <div className="mt-4">
                        <p className="text-xs text-red-300 mb-2">{L('La génération a échoué. Vous pouvez relancer la création.', 'Generation failed. You can restart it.')}</p>
                        <button onClick={() => { setSelectedPlanCalcId(c.calculation_id); setSelectedPlanPackage('meal_plan'); setSelectedPlanReturnView('plan'); setView('plan-detail'); }} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold border border-red-500/40 bg-red-500/15 text-red-200 hover:bg-red-500/25 transition-colors" data-testid="retry-plan-link"><RefreshCw className="w-3.5 h-3.5" /> {L('Réessayer', 'Retry')}</button>
                      </div>
                    )}
                  </motion.div>
                );
              })}
            </div>
          </section>
        )}
      </div>
    );
  }

  // view === 'history'
  return (
    <div data-testid="nutrition-history-view">
      <BackBtn />
      <h2 className="text-lg font-bold mb-4">{L('Vos calculs', 'Your calculations')}</h2>
      {loadErr && <p className="text-red-400 text-sm">{loadErr}</p>}
      {calcs === null && !loadErr && <div className="flex items-center gap-2 text-zinc-500 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> {L('Chargement…', 'Loading…')}</div>}
      {calcs && calcs.length === 0 && (
        <div className="rounded-2xl border border-white/10 bg-zinc-900/60 p-10 text-center" data-testid="empty-state">
          <Inbox className="w-10 h-10 text-zinc-600 mx-auto mb-3" />
          <p className="text-zinc-400">{L("Vous n'avez pas encore de calcul.", 'You have no calculation yet.')}</p>
          <Link to="/calculateur-calories" className="inline-flex items-center gap-1.5 mt-4 text-primary font-semibold hover:underline">{L('Lancer mon premier calcul', 'Start my first calculation')} <ArrowRight className="w-4 h-4" /></Link>
        </div>
      )}
      {calcs && calcs.length > 0 && (
        <div className="grid sm:grid-cols-2 gap-4">
          {calcs.map((c, i) => (
            <motion.div key={c.calculation_id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, delay: i * 0.04 }}
              className="rounded-2xl border border-white/10 bg-zinc-900/60 p-5 hover:border-primary/40 transition-colors" data-testid="calc-card">
              <div className="flex items-center justify-between gap-2 mb-3">
                <span className="inline-flex items-center gap-1.5 text-xs text-zinc-500"><Calendar className="w-3.5 h-3.5" /> {fmtDate(c.created_at)}</span>
                <div className="flex items-center gap-1.5">
                  {c.has_meal_plan ? <Badge color="yellow" icon={Sparkles}>{L('Plan', 'Plan')}</Badge> : c.has_pro ? <Badge color="pink" icon={Crown}>Pro</Badge> : <Badge color="zinc">{L('Gratuit', 'Free')}</Badge>}
                </div>
              </div>
              <p className="font-bold text-lg">{(GOAL_LABELS[c.goal] || [c.goal, c.goal])[en ? 1 : 0]}</p>
              <div className="flex items-center gap-4 mt-2 text-sm text-zinc-300">
                <span className="inline-flex items-center gap-1.5"><Flame className="w-4 h-4 text-primary" /> {Math.round(c.target_calories)} kcal</span>
                {c.protein_g != null && <span className="inline-flex items-center gap-1.5"><Beef className="w-4 h-4 text-pink-400" /> {Math.round(c.protein_g)}g</span>}
              </div>
              <button onClick={() => { setSelectedPlanCalcId(c.calculation_id); setSelectedPlanPackage(c.has_meal_plan ? 'meal_plan' : c.has_pro ? 'pro' : 'free'); setSelectedPlanReturnView('history'); setView('plan-detail'); }} className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:text-pink-400 transition-colors" data-testid="view-result-link">{L('Voir les résultats', 'View results')} <ArrowRight className="w-4 h-4" /></button>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};

export default NutritionHub;
